import React, { useEffect, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Button } from "../../components/ui/Button";
import { EnhancedPlan } from "../SubscriptionPage/types";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";

interface CheckoutSuccessProps {
    plan: EnhancedPlan;
    onDone?: () => void;
}

const CheckoutSuccess: React.FC<CheckoutSuccessProps> = ({ plan, onDone }) => {
    const navigate = usePartnerAwareNavigate();
    const [countdown, setCountdown] = useState(5);

    // Auto-navigate after delay
    useEffect(() => {
        const timer = setTimeout(() => {
            handleContinue();
        }, 5000);

        // Countdown timer
        const countdownInterval = setInterval(() => {
            setCountdown((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => {
            clearTimeout(timer);
            clearInterval(countdownInterval);
        };
    }, []);

    const handleContinue = () => {
        if (onDone) {
            onDone();
        } else {
            navigate("/");
        }
    };

    // Determine theme color based on plan type
    const getThemeColor = () => {
        if (plan.name.toLowerCase().includes("plus")) {
            return {
                gradient: "from-orange-400 to-orange-500",
                button: "bg-orange-500 hover:bg-orange-600",
                shadow: "shadow-orange-200",
            };
        } else if (plan.name.toLowerCase().includes("pro")) {
            return {
                gradient: "from-purple-400 to-purple-500",
                button: "bg-purple-500 hover:bg-purple-600",
                shadow: "shadow-purple-200",
            };
        } else {
            return {
                gradient: "from-blue-400 to-blue-500",
                button: "bg-blue-500 hover:bg-blue-600",
                shadow: "shadow-blue-200",
            };
        }
    };

    const themeColors = getThemeColor();

    return (
        <div
            className="fixed inset-0 bg-white z-[9999] flex flex-col items-center justify-center"
            style={{ animation: "fadeIn 0.5s ease-in-out forwards" }}>
            <div
                className="w-full max-w-2xl px-6"
                style={{ animation: "slideUp 0.8s ease-out forwards" }}>
                <div
                    className={`border border-gray-200 rounded-lg bg-white p-10 shadow-2xl ${themeColors.shadow}`}>
                    {/* Even larger animation container */}
                    <div className="w-96 h-96 mx-auto mb-8">
                        <DotLottieReact
                            src="https://lottie.host/cd10b060-5d7e-498d-95ff-35005ab7d8cf/UTbfzd7Z3C.lottie"
                            loop
                            autoplay
                        />
                    </div>

                    <h2
                        className={`text-4xl font-bold mb-4 text-gray-800 bg-gradient-to-r ${themeColors.gradient} bg-clip-text text-transparent`}>
                        Welcome to {plan.name}!
                    </h2>

                    <p className="text-gray-600 text-xl mb-8">
                        Your subscription has been successfully activated. You
                        now have access to all the {plan.name} plan features.
                    </p>

                    <div className="bg-gray-50 p-6 rounded-lg mb-10 border border-gray-100">
                        <h3 className="font-medium text-gray-800 mb-4 text-xl">
                            Your subscription includes:
                        </h3>
                        <ul className="text-left text-gray-600 space-y-4">
                            {plan.features.slice(0, 3).map((feature, idx) => (
                                <li key={idx} className="flex items-center">
                                    <svg
                                        className="w-6 h-6 mr-3 text-green-500"
                                        fill="currentColor"
                                        viewBox="0 0 20 20">
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className="text-lg">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <Button
                        onClick={handleContinue}
                        className={`w-full py-5 text-xl font-medium ${themeColors.button} text-white transition-all duration-300 transform hover:scale-[1.02] rounded-lg shadow-lg`}>
                        Go to Dashboard {countdown > 0 && `(${countdown})`}
                    </Button>

                    <p className="text-sm text-gray-500 mt-4">
                        You'll be automatically redirected in a few seconds.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CheckoutSuccess;
